import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Check, ChevronUp, Clock, Loader2 } from "lucide-react";

import ScrollX from "../ui/ScrollX";
import { formatBenefitYear, formatDate } from "../../utils/dates";

const money = (n) =>
    n === null || n === undefined ? "—" : `$${Number(n).toLocaleString("en-US")}`;

// Fee rate is stored as a percentage value (e.g. 12.00 = 12%).
const percent = (n) =>
    n === null || n === undefined ? null : `${Number(n)}%`;

const productMixLabel = (cat) => {
    const products = [cat.hasHsa && "HSA", cat.hasLsa && "LSA"].filter(Boolean);
    if (products.length === 0) return "—";
    return `${cat.isFlex ? "Flexed " : ""}${products.join(", ")}`;
};

const SectionLabel = ({ children }) => (
    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        {children}
    </h4>
);

const LimitRow = ({ limit }) => (
    <div className="flex items-center gap-2 whitespace-nowrap">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
            {limit.product}
        </span>
        <span className="text-xs text-gray-600">
            <span className="text-gray-400">S</span> {money(limit.single)}
            <span className="text-gray-400 ml-2">C</span> {money(limit.couple)}
            <span className="text-gray-400 ml-2">F</span> {money(limit.family)}
        </span>
    </div>
);

// Label stacks above the value on phones and sits opposite it once there's room.
const DetailRow = ({ label, children }) => (
    <div className="px-4 py-2 flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-1.5">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-medium text-gray-900 sm:text-right">{children}</span>
    </div>
);

const CarryForwardValue = ({ value }) =>
    value ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
            {value}
        </span>
    ) : (
        <span className="text-gray-400">None</span>
    );

const PanelShell = ({ onCollapse, children }) => (
    <div className="bg-gray-50 border-t border-gray-100 px-4 md:px-6 py-6">
        {children}
        <div className="mt-5 pt-4 border-t border-gray-200 flex justify-center">
            <button
                type="button"
                onClick={onCollapse}
                className="text-primary hover:text-primary-dark text-sm font-medium inline-flex items-center gap-1 transition-colors"
            >
                <ChevronUp className="w-4 h-4" />
                Collapse
            </button>
        </div>
    </div>
);

const EbBadge = ({ n }) => (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-200 flex-shrink-0">
        {n}
    </span>
);

const ClientSummaryPanel = ({ summaryUrl, expensesUrlFor, token, onCollapse }) => {
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setSummary(null);
        setError(false);

        const load = async () => {
            try {
                const response = await fetch(summaryUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error("Failed to load summary");
                const data = await response.json();
                if (!cancelled) setSummary(data);
            } catch {
                if (!cancelled) setError(true);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [summaryUrl, token]);

    if (error) {
        return (
            <PanelShell onCollapse={onCollapse}>
                <p className="text-sm text-gray-500">
                    Unable to load client summary. Please try again.
                </p>
            </PanelShell>
        );
    }

    if (!summary) {
        return (
            <PanelShell onCollapse={onCollapse}>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading client summary…
                </div>
            </PanelShell>
        );
    }

    const { feeSummary, policyDetails: pd, categories, planAdmins } = summary;
    const totalEmployees = categories.reduce((sum, c) => sum + (c.employees || 0), 0);
    const hsaRate = percent(feeSummary.hsaRate);
    const lsaRate = percent(feeSummary.lsaRate);

    // Keyed by name + limit: the same benefit can be offered to several categories at
    // different limits, and each distinct limit needs its own entry.
    const enhancedBenefitsList = [];
    const seenEb = new Set();
    categories.forEach((c) =>
        (c.enhancedBenefits || []).forEach((eb) => {
            const key = `${eb.name}|${eb.limit ?? ""}`;
            if (!seenEb.has(key)) {
                seenEb.add(key);
                enhancedBenefitsList.push(eb);
            }
        })
    );

    const anyHsa = categories.some((c) => c.hasHsa);
    const anyLsa = categories.some((c) => c.hasLsa);
    const anyProduct = anyHsa || anyLsa;
    const anyCoreHealth = categories.some((c) => c.coreHealth);
    const anyScheduledCredits = categories.some((c) => c.scheduledCredits);
    const anyEb = enhancedBenefitsList.length > 0;
    const showExpenses = anyProduct || anyEb;
    // Optional columns in render order: Product Mix, CoreHealth, Scheduled Credits,
    // Limits, Eligible Expenses, Enhanced Benefits (on top of Category + Employees).
    const columnCount =
        2 +
        [anyProduct, anyCoreHealth, anyScheduledCredits, anyProduct, showExpenses, anyEb].filter(
            Boolean
        ).length;

    // Fetch the eligible-expenses PDF with auth, then open it in a new tab.
    // The blank tab is opened synchronously so the popup blocker allows it.
    const openExpensesPdf = async (limitId) => {
        if (!limitId) return;
        const tab = window.open("", "_blank");
        try {
            const response = await fetch(expensesUrlFor(limitId), {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Failed to load expenses PDF");
            const url = URL.createObjectURL(await response.blob());
            if (tab) tab.location = url;
            else window.open(url, "_blank");
        } catch {
            if (tab) tab.close();
            toast.error("Unable to open eligible expenses.");
        }
    };

    return (
        <PanelShell onCollapse={onCollapse}>
            <div className="space-y-6">
                <div
                    className={`grid grid-cols-1 gap-6 items-stretch ${
                        anyProduct ? "md:grid-cols-[minmax(180px,auto)_1fr]" : ""
                    }`}
                >
                    {anyProduct && (
                        <div className="flex flex-col">
                            <SectionLabel>Fee Summary</SectionLabel>
                            <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 flex-1 flex flex-col justify-center gap-2">
                                <div className="text-xs text-gray-500">Admin fee rate</div>
                                {(anyHsa && hsaRate) || (anyLsa && lsaRate) ? (
                                    <div className="space-y-1.5">
                                        {anyHsa && hsaRate && (
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="text-2xl font-bold text-gray-900">{hsaRate}</span>
                                                <span className="text-xs font-medium text-gray-500">HSA</span>
                                            </div>
                                        )}
                                        {anyLsa && lsaRate && (
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="text-2xl font-bold text-gray-900">{lsaRate}</span>
                                                <span className="text-xs font-medium text-gray-500">LSA</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-2xl font-bold text-gray-400">—</div>
                                )}
                                <div className="text-xs text-gray-500">of eligible claims</div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col">
                        <SectionLabel>Policy Details</SectionLabel>
                        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 flex-1">
                            <DetailRow label="Benefit year">
                                {formatBenefitYear(pd.benefitYearStart, pd.benefitYearEnd)}
                                {pd.renewalDate && ` · Renews ${formatDate(pd.renewalDate)}`}
                            </DetailRow>
                            <DetailRow label="Run-off period">
                                {pd.runOffMonths != null ? `${pd.runOffMonths} months` : "—"}
                            </DetailRow>
                            {anyHsa && (
                                <DetailRow label="Carry forward — HSA">
                                    <CarryForwardValue value={pd.carryForwardHsa} />
                                </DetailRow>
                            )}
                            {anyLsa && (
                                <DetailRow label="Carry forward — LSA">
                                    <CarryForwardValue value={pd.carryForwardLsa} />
                                </DetailRow>
                            )}
                            {pd.scheduledCreditsEnabled && (
                                <DetailRow label="Scheduled credits frequency">
                                    {pd.scheduledCreditsFrequency || "Enabled"}
                                </DetailRow>
                            )}
                            <DetailRow label="Active enrolled employees">
                                {pd.activeEnrolledEmployees}
                            </DetailRow>
                        </div>
                    </div>
                </div>

                <div>
                    <SectionLabel>Benefit Categories &amp; Limits</SectionLabel>
                    <ScrollX className="bg-white border border-gray-200 rounded-xl">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <th className="text-left py-3 px-4">Category</th>
                                    <th className="text-left py-3 px-4">Employees</th>
                                    {anyProduct && <th className="text-left py-3 px-4">Product Mix</th>}
                                    {anyCoreHealth && <th className="text-left py-3 px-4">CoreHealth</th>}
                                    {anyScheduledCredits && (
                                        <th className="text-left py-3 px-4">Scheduled Credits</th>
                                    )}
                                    {anyProduct && <th className="text-left py-3 px-4">Limits</th>}
                                    {showExpenses && (
                                        <th className="text-left py-3 px-4">Eligible Expenses</th>
                                    )}
                                    {anyEb && (
                                        <th className="text-left py-3 px-4 w-44">Enhanced Benefits</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {categories.length === 0 ? (
                                    <tr>
                                        <td colSpan={columnCount} className="py-6 text-center text-sm text-gray-400">
                                            No benefit categories found.
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {categories.map((cat) => {
                                            const ebs = cat.enhancedBenefits || [];
                                            return (
                                                <tr key={cat.name}>
                                                    <td className="py-3 px-4 font-semibold text-gray-900 align-top">{cat.name}</td>
                                                    <td className="py-3 px-4 text-gray-700 align-top">{cat.employees}</td>
                                                    {anyProduct && (
                                                        <td className="py-3 px-4 align-top">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                                                {productMixLabel(cat)}
                                                            </span>
                                                        </td>
                                                    )}
                                                    {anyCoreHealth && (
                                                        <td className="py-3 px-4 align-top">
                                                            {cat.coreHealth ? (
                                                                <Check className="w-4 h-4 text-primary" />
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {anyScheduledCredits && (
                                                        <td className="py-3 px-4 align-top">
                                                            {cat.scheduledCredits ? (
                                                                <Check className="w-4 h-4 text-primary" />
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {anyProduct && (
                                                        <td className="py-3 px-4 align-top">
                                                            {cat.limits.length > 0 ? (
                                                                <div className="space-y-1">
                                                                    {cat.limits.map((limit) => (
                                                                        <LimitRow key={limit.product} limit={limit} />
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {showExpenses && (
                                                        <td className="py-3 px-4 align-top">
                                                            <button
                                                                type="button"
                                                                onClick={() => openExpensesPdf(cat.limitId)}
                                                                disabled={!cat.limitId}
                                                                className="text-primary hover:text-primary-dark text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                            >
                                                                View expenses
                                                            </button>
                                                        </td>
                                                    )}
                                                    {anyEb && (
                                                        <td className="py-3 px-4 align-top w-44">
                                                            {ebs.length > 0 ? (
                                                                <div className="flex flex-col items-start gap-1">
                                                                    {ebs.map((eb, i) => (
                                                                        <span
                                                                            key={i}
                                                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap"
                                                                        >
                                                                            {eb.limit != null ? `${eb.name} · ${money(eb.limit)}` : eb.name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                        <tr className="bg-gray-50/50">
                                            <td className="py-3 px-4 text-gray-500">Total</td>
                                            <td className="py-3 px-4 font-bold text-gray-900">{totalEmployees}</td>
                                            {columnCount > 2 && <td colSpan={columnCount - 2} />}
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </ScrollX>
                </div>

                {anyEb && (
                    <div>
                        <SectionLabel>Enhanced Benefits</SectionLabel>
                        <div className="space-y-3">
                            {enhancedBenefitsList.map((eb, i) => (
                                <div
                                    key={i}
                                    className="bg-white border border-amber-500 rounded-xl px-4 py-3"
                                >
                                    <div className="flex items-center gap-2">
                                        <EbBadge n={i + 1} />
                                        <span className="font-semibold text-gray-900">{eb.name}</span>
                                        {eb.limit != null && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                                {money(eb.limit)}
                                            </span>
                                        )}
                                    </div>
                                    {eb.description && (
                                        <p className="text-sm text-gray-600 mt-2">{eb.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <SectionLabel>Plan Administrators</SectionLabel>
                    {planAdmins.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {planAdmins.map((admin) => (
                                <div
                                    key={admin.email}
                                    className="bg-white border border-gray-200 rounded-xl px-4 py-3"
                                >
                                    <div className="font-semibold text-gray-900 truncate">{admin.name}</div>
                                    <div className="text-sm text-gray-500 break-all">{admin.email}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400">No plan administrators found.</p>
                    )}
                </div>

                <div>
                    <SectionLabel>Reports</SectionLabel>
                    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-500">
                            {"Client reports are coming soon. You'll be able to pull and download utilization and disbursement reports for each policy directly from this portal."}
                        </span>
                    </div>
                </div>
            </div>
        </PanelShell>
    );
};

export default ClientSummaryPanel;
